import React, { useState, useEffect } from 'react'
import { OmForm, omMessage } from '@owma/kit'
import type { OmFormInstance } from '@owma/kit'

import i18n from 'i18n'
import classNames from 'classnames'
import conf from 'conf'
import { buildSignatureEml, base64utf8String } from 'libs/eml-format'
import type { SignatureEmlAttachment } from 'libs/eml-format/interface'
import jamExt from 'libs/jamUtil/ext'
import { getSafeHTML, openFile, getUniqueKey, blobToDataUrl } from 'utils'
import {
  convertCidImagesToDataUrl,
  convertPastedImageSources,
  base64ToBlobFile,
  dataUrlToBlobFile,
  resolveOrCreateDataUrlCid,
} from 'utils/imageProcessor'
import { prepareSignatureBodyHtml } from 'utils/mailBodyHelpers'
import { formatBytes, confirmBase, errorBase } from 'utils/ui'
import parse from 'html-react-parser'

import { IframeHost, TinyMCEAdapter } from 'libs/editor/tinymce'
import { NgwEditorAdapter } from '@owma/common-web/components/editor/ngweditor'
import { buildEditorInitOptions } from '@owma/common-web/components/editor'
import { safeUUID } from 'utils/safeUUID'
import type {
  EditorDefaultStyle,
  EditorPreProcessEvent,
  HasNativeToolbarResult,
  IEditorEventHandler,
  IMailEditor,
  ToolbarPropsChangePayload,
} from '@owma/common-web/components/editor'
import { editorType } from 'vars'

import { store as reduxStore } from 'index'

import attachmentHelper from 'pages/main/components/Attachment/helper'

import editorToolbar from 'pages/main/components/EmailForm/editorToolbar'
import ImageToolbar from 'pages/main/components/EmailForm/ImageToolbar'

import syncAniGIF from 'assets/images/gif/loading.gif'

import styles from './style.module.scss'

interface AttachmentFile {
  name: string
  type?: string
  size: number
  isInline?: boolean
  cid?: string
  contentId?: string
  disposition?: string
}

interface Attachment {
  file: AttachmentFile
  download: () => Promise<{ blob: Blob }>
  isUsed?: boolean
  downloadObj?: { blob: Blob; file: File }
}

interface SignatureData {
  html?: string
  content?: string
  attachments?: AttachmentFile[]
}

interface SignatureEditProps {
  innerRef?: React.RefObject<any>
  signature?: SignatureData
  readonly?: boolean
  onModifiedChange?: (modified: boolean) => void
}

interface SignatureEditState {
  invisible: boolean
}

/**
 * HTML 안의 모든 `<img>` 의 src → {width, height} 매핑을 추출. NgwEditor 의 image NodeSpec
 * parseDOM 과 동일한 우선순위 (inline style.width → attribute width → element.width).
 *
 * SignatureEdit 의 setData 직전에 호출하여 EML 원본 width/height 를 매핑으로 보관 → setData
 * 후 PM doc 의 image attrs 가 ImageNodeView race 로 변형된 경우 본 매핑으로 복원한다.
 */
function extractImageDimensions(html: string): Map<string, { width: number; height: number }> {
  const map = new Map<string, { width: number; height: number }>()
  const doc = new DOMParser().parseFromString(html, 'text/html')
  doc.querySelectorAll('img').forEach((img) => {
    const src = img.getAttribute('src')
    if (src == null || src.length === 0) return
    let width: number | null = null
    let height: number | null = null
    if (img.style.width) {
      width = parseInt(img.style.width, 10) || null
    } else {
      const attr = img.getAttribute('width')
      if (attr != null) width = parseInt(attr, 10) || null
    }
    if (img.style.height) {
      height = parseInt(img.style.height, 10) || null
    } else {
      const attr = img.getAttribute('height')
      if (attr != null) height = parseInt(attr, 10) || null
    }
    if (width != null && height != null) {
      map.set(src, { width, height })
    }
  })
  return map
}

class SignatureEdit
  extends React.Component<SignatureEditProps, SignatureEditState>
  implements IEditorEventHandler
{
  state: SignatureEditState = {
    invisible: true,
  }

  inlineAttachmentMap: Record<string, Attachment> = {}

  srcImageMap: Record<string, any> = {}

  processImageMap: Record<string, any> = {}

  toolbarRef = React.createRef<OmFormInstance>()

  language: string = i18n.language

  modified: boolean = false

  attachmentHelper = attachmentHelper

  attachmentsLimit: number = 500 * 1024

  iframe?: HTMLIFrameElement | null

  editElem?: React.ReactElement

  curValues?: any

  searchTimer?: NodeJS.Timeout

  editorContainer?: HTMLDivElement

  editorInitStarted: boolean = false

  isEditorInited: boolean = false

  // PR-8 (~PR-N7): 어댑터 인스턴스. EmailForm 의 PR-7a-2 패턴과 동일 — vars.editorType 분기로
  // TinyMCEAdapter / NgwEditorAdapter 중 선택. 두 어댑터 모두 IMailEditor 인터페이스를 구현하므로
  // 호출부는 어댑터 종류와 무관.
  editorAdapter: IMailEditor = editorType === 'ngw' ? new NgwEditorAdapter() : new TinyMCEAdapter()

  componentDidMount(): void {
    const { innerRef } = this.props
    i18n.on('languageChanged', this.onLanguageChanged)

    if (innerRef != null) {
      ;(innerRef as React.MutableRefObject<any>).current = this
    }

    this.editorAdapter.lifecycle.setEventHandler(this)
  }

  componentWillUnmount(): void {
    const { innerRef } = this.props
    i18n.off('languageChanged', this.onLanguageChanged)

    if (innerRef != null) {
      ;(innerRef as React.MutableRefObject<any>).current = null
    }

    if (this.iframe != null) {
      this.editorAdapter.state.clearObservers()
    }

    if (this.editorInitStarted === true) {
      try {
        this.editorAdapter.lifecycle.dispose()
      } catch (err) {
        console.error('SignatureEdit: editor lifecycle.dispose failed', err)
      }
    }

    Object.values(this.processImageMap).forEach((value) => {
      value.infoList.forEach((info: any) => {
        if (info != null && info.url != null) {
          window.URL.revokeObjectURL(info.url)
        }
      })
    })
  }

  onLanguageChanged = (language: string): void => {
    this.language = language
    this.applyLanguage()
  }

  applyLanguage = (): void => {
    const lang = this.language
    const locale: 'ko-KR' | 'ja-JP' | 'en-US' =
      lang === 'ko-KR' ? 'ko-KR' : lang === 'ja-JP' ? 'ja-JP' : 'en-US'
    this.editorAdapter.lifecycle.setLocale(locale)
  }

  createRefObj = (): any => {
    return {
      reactObj: this,
      getContent: async () => {
        return await this.getContent()
      },
    }
  }

  // 인라인 이미지가 base64 로 인코딩됐을 때의 바이트 길이(3byte→4char, 4*ceil(n/3)). 실제 인코딩
  // 없이 원본 크기만으로 계산한다. 크기 검사에서 platform 의 data URI 인라인 기준과 맞추기 위함.
  private base64ByteLength = (data: SignatureEmlAttachment['data']): number => {
    let rawBytes: number
    if (typeof data === 'string') {
      rawBytes = data.length
    } else if (data instanceof Blob) {
      rawBytes = data.size
    } else {
      rawBytes = data.byteLength
    }
    return 4 * Math.ceil(rawBytes / 3)
  }

  getContent = async (): Promise<{
    eml: string | null
    error: any
    sizeExceeded?: boolean
  }> => {
    if (this.editorAdapter.lifecycle.isReady() !== true) {
      return Promise.resolve({ eml: null, error: null })
    }

    // PR-N9a-2: 서명 저장 경로 — urlMap 역치환 + onPreProcess 발화 포함.
    // NgwEditor 모드에서 data URL → cid:/blob: 역치환이 본 메서드 내부에서 처리됨.
    const body = this.editorAdapter.content.getDataPreprocessed()
    const attachmentList = Object.values(this.inlineAttachmentMap).map((item) => item)

    const html = prepareSignatureBodyHtml(body, editorType)

    let attachments: SignatureEmlAttachment[] | undefined
    if (attachmentList != null && attachmentList.length > 0) {
      attachments = await Promise.all(
        attachmentList.map(async (attachment) => {
          const { name, type, isInline, cid } = attachment.file
          const downloadObj = await attachment.download()
          const ret: SignatureEmlAttachment = {
            filename: base64utf8String(name),
            data: downloadObj.blob,
          }
          if (type != null) {
            ret.contentType = type
          }
          if (isInline === true) {
            ret.inline = true
          }
          if (cid != null) {
            ret.cid = cid
          }
          return ret
        }),
      )
    }

    // 크기 검사는 officemail-platform 과 동일하게 "인라인 이미지를 포함한 서명 콘텐츠" 바이트를
    // 기준으로 한다. platform 은 data URI 가 인라인된 HTML 바이트를 재지만, web 은 이미지를 cid 로
    // 분리하므로 본문 HTML 바이트 + 각 인라인 이미지의 base64 바이트로 같은 총량을 계산한다.
    // MIME 경계·파트 헤더 같은 EML 봉투 오버헤드는 platform 검사에 없으므로 제외해야 platform 에서
    // 통과한 서명이 web 에서도 통과한다.
    const contentByteSize =
      new TextEncoder().encode(html).length +
      (attachments ?? []).reduce((sum, att) => sum + this.base64ByteLength(att.data), 0)
    if (contentByteSize >= this.signatureSizeLimit) {
      const limit = formatBytes(this.signatureSizeLimit, 0, false, 'KB')
      const size = formatBytes(contentByteSize, 1, false, 'KB')
      errorBase({
        title: i18n.t('common.error.title'),
        content: i18n.t('signature.save.sizeError', { limit, size }),
        width: 450,
      })
      return { eml: null, error: null, sizeExceeded: true }
    }

    // 서명 EML 은 officemail-platform 과 동일한 raw UTF-8 포맷으로 생성한다. 공용 buildEml 은
    // 본문을 quoted-printable 로 인코딩해 한글이 ~3배 팽창하지만, buildSignatureEml 은 raw 로 넣어
    // platform 이 저장한 서명을 재저장해도 크기가 유지되도록 한다.
    const { eml, error } = await buildSignatureEml(html, attachments)

    if (error != null) {
      console.error('build error - ', error)
    }

    return { eml, error }
  }

  /**
   * PR-8: `<div ref={setEditorContainer}>` 의 ref 콜백. EmailForm 의 PR-7a-3 패턴과 동일.
   *
   * 컨테이너 div 가 마운트되면 어댑터 `lifecycle.init({ host: 'iframe' })` 을 호출하여
   * `IframeHost` 가 div 안에 iframe 을 동적으로 생성하도록 한다.
   */
  setEditorContainer = (ref: HTMLDivElement | null): void => {
    if (ref == null) return
    if (this.editorInitStarted === true) return
    this.editorInitStarted = true
    this.editorContainer = ref

    const hash = conf.gitInfo.commit.hash
    const htmlUrl = 'editor/tinyMCE_desktop.html?hash=' + hash

    // PR-N10 (0.2.11): NgwEditor 어댑터는 init 시점에 nineMailId 필요. SignatureEdit 은
    // 매 편집 세션마다 새 UUID — 외부 노출 없으므로 충돌 무해.
    const signatureNineMailId = `signature-edit-${safeUUID()}`
    // C7: NgwEditor 한정으로 첫 마운트부터 사용자 기본 폰트 적용. TinyMCE 어댑터는 본 인자를
    // 무시하므로 ngw 분기에서만 computeDefaultStyle 호출 (불필요 계산 회피).
    const defaultStyle = editorType === 'ngw' ? this.computeDefaultStyle() : undefined
    this.editorAdapter.lifecycle
      .init(buildEditorInitOptions(editorType, ref, htmlUrl, signatureNineMailId, defaultStyle))
      .then(() => {
        // 'ngw' 모드는 inline 마운트라 iframe 이 존재하지 않는다. 기존 `this.iframe != null` 가드가
        // 그대로 동작하도록 null 유지 — 'tinymce' (iframe) 모드 전용 경로.
        if (editorType !== 'ngw') {
          const host = this.editorAdapter.host as IframeHost
          this.iframe = host.getIframeElement()
        }
      })
      .catch((err: unknown) => {
        console.error('SignatureEdit: editor lifecycle.init failed', err)
      })
  }

  getTotalAttachmentSize = (): number => {
    let totalSize = 0
    Object.values(this.inlineAttachmentMap).forEach((attachment) => {
      totalSize += attachment.file.size
    })

    return totalSize
  }

  addInlineAttachment = (attachment: Attachment, cid: string): void => {
    this.inlineAttachmentMap[cid] = attachment
  }

  getInlineAttachment = (cid: string): Attachment | undefined => {
    return this.inlineAttachmentMap[cid]
  }

  setEmlAttachments = (attachments: AttachmentFile[]): void => {
    this.inlineAttachmentMap = {}

    attachments.forEach((file) => {
      const { contentId } = file
      if (contentId != null) {
        const attachmentObj: any = this.attachmentHelper.createAttachment(
          file as any,
          contentId,
          this as any,
          true,
        )
        this.addInlineAttachment(attachmentObj, contentId)
      }
    })
  }

  getEditElem = (): React.ReactElement => {
    if (this.editElem == null) {
      this.editElem = (
        <>
          <div
            className={classNames(styles.htmlEditor, 'htmlEditor')}
            ref={(ref) => {
              this.setEditorContainer(ref)
            }}
          />
          {/* editor-image-tools — 이미지 선택 시 회전/반전/크기 Floating 툴바(ngwEditor 전용). */}
          <ImageToolbar editorImage={this.editorAdapter.image} enabled={editorType === 'ngw'} />
        </>
      )
    }

    return this.editElem
  }

  setVisible = (visible: boolean): void => {
    this.setState({ invisible: visible === false })
  }

  initFocus = (): void => {
    setTimeout(() => {
      this.setVisible(true)

      if (this.editorAdapter.lifecycle.isReady() === true) {
        this.editorAdapter.host.focus()
        this.editorAdapter.cursor.setIntoNBodyCore(false)
      }
    }, 200)
  }

  isEmpty = (): boolean => {
    if (this.editorAdapter.lifecycle.isReady() === true) {
      if (this.editorAdapter.content.isEmpty() !== true) {
        return false
      }
    }

    return true
  }

  isModified = (): boolean => {
    if (this.editorAdapter.lifecycle.isReady() === true) {
      if (
        this.editorAdapter.mail.region.nBody.isChanged() === true ||
        this.editorAdapter.mail.region.quotedBody.isChanged() === true
      ) {
        return true
      }
    }
    return false
  }

  /**
   * Editor 의 onChange 마다 호출. modified 상태가 변할 때 callback 으로 ManageSignature 에 알림.
   *
   * 2026-05-19 수정: 이전엔 `if (this.modified === false)` 가드로 첫 입력에서 isModified 가 false
   * 반환되면 그 후 영구히 isModified 재검사 안 함. 사용자 보고로 가드 제거 — 매 호출 isModified
   * 재검사 + 상태 토글 시 callback. modified 가 true → false (사용자가 본문 다 지움) 케이스도
   * 정상 추적.
   */
  onInputChanged = (): void => {
    const next = this.isModified()
    if (next === this.modified) return
    this.modified = next
    const { onModifiedChange } = this.props
    if (onModifiedChange != null) {
      onModifiedChange(next)
    }
  }

  onToolbarChange = (values: any, allValues: any): void => {
    console.log(' - onToolbarChange - ', values, allValues)

    if (this.editorAdapter.lifecycle.isReady() !== true) {
      return
    }

    this.editorAdapter.host.focus()
    if (
      this.editorAdapter.content.isEmpty() === true &&
      this.editorAdapter.cursor.isCollapsed() === true
    ) {
      this.editorAdapter.cursor.setIntoNBodyCore()
    }

    Object.entries(values).forEach(([key, value]) => {
      this.dispatchToolbarCommand(key, value)
    })
    this.editorAdapter.host.focus()
    this.editorAdapter.cursor.focus()
  }

  /**
   * PR-8: 기존 `editorExt.ts.runCommand` 24-case switch 를 어댑터 `IEditorCommand` 호출로 풀어
   * 쓴 thin dispatcher. EmailForm 의 dispatchToolbarCommand 와 동일 매핑.
   */
  private dispatchToolbarCommand = (key: string, value: unknown): void => {
    const command = this.editorAdapter.command
    switch (key) {
      case 'fontname':
        command.run('fontFamily', value as string)
        break
      case 'fontsize':
        command.run('fontSize', value as string)
        break
      case 'bold':
        command.run('bold')
        break
      case 'italic':
        command.run('italic')
        break
      case 'underline':
        command.run('underline')
        break
      case 'strikethrough':
        command.run('strikethrough')
        break
      case 'removeformat':
        command.run('removeFormat')
        break
      case 'alignleft':
        command.run('alignLeft')
        break
      case 'aligncenter':
        command.run('alignCenter')
        break
      case 'alignright':
        command.run('alignRight')
        break
      case 'forecolor':
        command.run('foregroundColor', value as string)
        break
      case 'backcolor':
        command.run('backgroundColor', value as string)
        break
      case 'orderedlist':
        command.run('orderedList')
        break
      case 'unorderedlist':
        command.run('unorderedList')
        break
      case 'indent':
        command.run('indent')
        break
      case 'outdent':
        command.run('outdent')
        break
      case 'link':
        command.run('link')
        break
      case 'insertimage':
        this.editorAdapter.image.openNativePicker()
        break
      case 'insertstyledhr': {
        const style = typeof value === 'string' ? value : 'solid'
        command.run('horizontalRule', { style })
        break
      }
      case 'inserttable':
        command.run('insertTable', { rows: 3, columns: 3 })
        break
      case 'blockquote':
        command.run('blockquote')
        break
      case 'ltr':
        command.run('directionLTR')
        break
      case 'rtl':
        command.run('directionRTL')
        break
      case 'undo':
        command.run('undo')
        break
      case 'redo':
        command.run('redo')
        break
      default:
        break
    }
  }

  onToolbarPropChanged = (fStr: string, bStr: string): void => {
    console.log(' --- toolbarPropsChanged : ', fStr, bStr)
    const values: any = {}
    fStr.split(';').forEach((item) => {
      const [key, value] = item.split(':')
      const val = value === 'true' ? true : value === 'false' ? false : value
      values[key.toLowerCase()] = val
    })

    bStr.split(';').forEach((item) => {
      const [key, value] = item.split(':')
      const val = value === 'true' ? true : value === 'false' ? false : value
      values[key.toLowerCase()] = val
    })

    if (values.fontname != null) {
      const fontList = values.fontname.split(',')
      const trimFontList = fontList.map((item: string) => {
        return item.trim()
      })
      values.fontname = trimFontList.join(', ')
    }

    if (this.toolbarRef.current != null) {
      this.toolbarRef.current.setFieldsValue(values)
    }
  }

  getEditorToolbarElem = (): React.ReactElement => {
    const toolbarElems = (editorToolbar as any).getToolbarMenu(styles, this.curValues || {}, this)

    if (this.curValues == null) {
      this.curValues = editorToolbar.getDefaultValues()
    }

    const formAttrs = {
      className: classNames(styles.editorToolbarForm),
      layout: 'horizontal' as const,
      ref: this.toolbarRef,
      onValuesChange: this.onToolbarChange,
      initialValues: this.curValues,
    }

    return (
      <div
        className={classNames('editorToolbar', styles.editorToolbar, styles.editorToolbarContainer)}
      >
        <OmForm {...formAttrs}>{toolbarElems}</OmForm>
      </div>
    )
  }

  setDivBody = (): void => {}

  getReadOnlyElem = (): React.ReactElement | null => {
    const { signature } = this.props

    if (signature != null) {
      const { html: initialHtml } = signature

      const HtmlView = (): React.ReactElement => {
        const [html, setHtml] = useState<React.ReactNode | null>(null)

        useEffect(() => {
          getSafeHTML(initialHtml!).then((newHtml: string) => {
            const htmlElem = parse(newHtml)
            setHtml(htmlElem)
          })
        }, [])

        return (
          <div className={classNames(styles.htmlMailBody, 'htmlMailBody', 'htmlEditor')}>
            {html}
          </div>
        )
      }
      return <HtmlView />
    }

    return null
  }

  // ===========================================================================
  // PR-8 — IEditorEventHandler 콜백 + 흡수된 helper 클로저
  //
  // editorExt.ts 의 NineNative literal 메서드 본체와 그 클로저 helper
  // (getBlobURL / base64ToBlob / dataURLtoBlob / convertCid / createImageInfo /
  // applyEditorSettings) 를 SignatureEdit 클래스 멤버로 흡수. EmailForm 의
  // PR-7a-2 패턴과 동일. 어댑터의 `image.attachBlob` 이 내부에서 blobId 를 생성하므로
  // 기존 editorExt.ts 의 module-scope `uniqueBlobId` / `blobIdCounter` 는 제거.
  // ===========================================================================

  private getBlobURL = (blob: Blob, filename: string): string => {
    const { uri } = this.editorAdapter.image.attachBlob(blob, { name: filename })
    return uri
  }

  private base64ToBlob = (base64Str: string, mime: string): File =>
    base64ToBlobFile(base64Str, mime)

  private dataURLtoBlob = (dataurl: string): File => dataUrlToBlobFile(dataurl)

  private convertCid = (bodyHTML: string): string => {
    const domParser = new DOMParser()
    const doc = domParser.parseFromString(bodyHTML, 'text/html')
    const imgList = doc.querySelectorAll('img')

    imgList.forEach((img) => {
      if (img.src.toLowerCase().indexOf('cid:') === 0) {
        img.setAttribute('data-cid', img.src)
        img.src = ''
      }
    })

    const lowerText = bodyHTML.toLowerCase()
    if (lowerText.indexOf('<!doctype') > -1 || lowerText.indexOf('<!DOCTYPE') > -1) {
      return new XMLSerializer().serializeToString(doc)
    }

    return doc.body.innerHTML
  }

  /**
   * PR-N9a-1 — NgwEditor 모드 전용 cid 이미지 변환 (SignatureEdit 판).
   *
   * EmailForm 의 `convertCidNgw` 와 동일 로직. signature 의 inline attachment 는
   * `setEmlAttachments` (L275~290) 가 `isEML=true` 로 생성하여 `fileForEML.content` (ArrayBuffer)
   * 가 메모리에 보유되므로 `attachment.download()` 가 즉시 resolve — 네트워크 없음.
   */
  private convertCidNgw = (bodyHTML: string): Promise<string> =>
    convertCidImagesToDataUrl(
      bodyHTML,
      (cid) => this.inlineAttachmentMap[cid],
      (cidSrc, dataUrl) => this.editorAdapter.image.registerUrlMapping(cidSrc, dataUrl),
    )

  private createImageInfo = (file: File, url: string, callbackStr?: string): any => {
    if (this.processImageMap[url] == null) {
      const hostWindow: any = this.editorAdapter.lifecycle.isReady()
        ? this.editorAdapter.host.getWindow()
        : null
      const imageInfo: any = {
        infoList: Array.from({ length: 16 }, () => null),
        imgObj: new Image(),
        file,
      }

      this.processImageMap[url] = imageInfo

      imageInfo.infoList[0] = { url, blob: file }

      imageInfo.imgObj.onload = () => {
        const arr = [url]
        const str = JSON.stringify(arr)

        if (callbackStr != null && hostWindow != null) {
          const callbackArr = callbackStr.split('.')
          let funcObj: any = hostWindow
          callbackArr.forEach((item) => {
            funcObj = funcObj[item]
          })
          funcObj(str)
        }
      }

      imageInfo.imgObj.src = url

      this.srcImageMap[url] = imageInfo
    }

    return this.processImageMap[url]
  }

  /**
   * C7: 서명 편집기의 기본 폰트 산출. EmailForm 과 달리 isNewEmail 분기 없이 항상
   * `editorFontColorNew` 사용 + fallback `#000000`. editorFont 접근도 props 가 아닌
   * `reduxStore.getState()` 직접 접근 (signature 컴포넌트는 connect 되지 않음).
   * setEditorContainer 의 init 시점과 applyEditorSettings 의 setDefaultStyle 호출
   * 시점이 본 메서드를 공유 — `areDefaultsEqual` NoOp 가드가 동일 값 재호출 시 재마운트 차단.
   */
  private computeDefaultStyle = (): EditorDefaultStyle => {
    const { settings: reduxSettings } = reduxStore.getState() as any
    const { editorFont } = reduxSettings.email
    const { editorFontFamily, editorFontSizeString, editorFontColorNew } = editorFont

    const fontFamily = editorFontFamily?.trim()
      ? editorFontFamily
      : 'Calibri, Arial, Helvetica, sans-serif'
    const fontSize = editorFontSizeString?.trim() ? editorFontSizeString : '11pt'
    const fontColor = editorFontColorNew?.trim() ? editorFontColorNew : '#000000'

    return { fontFamily, fontSize, color: fontColor }
  }

  private applyEditorSettings = (): void => {
    if (this.editorAdapter.lifecycle.isReady() !== true) return

    // C7: 폰트 계산 단일 소스 — `computeDefaultStyle` 호출. setEditorContainer 의 init 단계에서
    // 동일 값이 이미 적용됐다면 `areDefaultsEqual` NoOp 가드가 재마운트를 차단.
    this.editorAdapter.content.setDefaultStyle(this.computeDefaultStyle())

    this.editorAdapter.cursor.setIntoNBodyCore()
    this.editorAdapter.state.resetChangedState()
  }

  // === IEditorEventHandler 콜백 ===

  onReady = (): void => {
    console.log(' --- onReady (SignatureEdit)')
    if (this.editorAdapter.lifecycle.isReady() !== true) return

    // editor.on('input'/'change') 시 어댑터 bindEditorEvents 가 IEditorEventHandler.onInputChanged
    // 콜백을 직접 발화하므로 (TinyMCELifecycle 의 input/change 핸들러) modified 추적은
    // SignatureEdit.onInputChanged 가 자동으로 호출된다. SignatureEdit 는 noInputTime 기반
    // 자동 저장이 없으므로 setNoInputTime 호출 불필요.
    this.applyLanguage()

    if (this.isEditorInited !== true) {
      this.isEditorInited = true
      this.editorAdapter.mail.resetAcexMatches()
    }

    const { signature } = this.props

    if (signature != null) {
      const { html, attachments } = signature

      if (attachments != null && attachments.length > 0) {
        this.setEmlAttachments(attachments)
      }

      const onLoaded = (): void => {
        this.editorAdapter.state.resetUndoHistory()
        // 서명 편집기는 quoted head/body 를 항상 펼친 상태로 표시한다. 서명 HTML 이 (답장 초안
        // 복사 등으로) fold 클래스가 붙은 quoted 마크업을 포함하면 setData 의 parseDOM 이 접힌
        // 상태로 복원하므로, load 직후 강제로 펼친다. quoted 영역이 없으면 no-op.
        // resetChangedState 이전에 호출해 펼침 transaction 이 유발한 dirty 를 baseline 으로 흡수.
        this.editorAdapter.mail.setQuotedCollapsed(false)
        // 2026-05-19 — 본문 load 완료 시점에 modified baseline 설정. setData / nBody.set 이 PM
        // onChange 를 발화하여 markDirty 가 호출되므로, baseline 없이는 init 직후에도 isDirty
        // 폴백으로 isChanged true 가 되어 저장 버튼이 잘못 enable 됨. resetChangedState 가
        // adapter.clearDirty + region.resetSnapshot 둘 다 수행 → 현재 본문이 baseline.
        this.editorAdapter.state.resetChangedState()
        this.modified = false
        this.initFocus()
      }

      if (html != null) {
        // PR-N9a-1: NgwEditor 모드 분기. signature 의 inline attachment 는 setEmlAttachments 가
        // 메모리 보유 형태 (isEML=true) 로 등록하므로 convertCidNgw 내부의 download() 가 즉시
        // resolve. TinyMCE 모드는 기존 convertCid 동기 호출.
        const convertPromise: Promise<string> =
          editorType === 'ngw' ? this.convertCidNgw(html) : Promise.resolve(this.convertCid(html))

        if (this.iframe != null) {
          this.iframe.style.visibility = 'hidden'
        }
        convertPromise.then((newHtml) => {
          // 2026-05-19 — `mail.region.nBody.set` 대신 `content.setData` 사용.
          //
          // signature 의 본문은 답장/quoted 영역이 없는 단일 본문 영역. nBody.set 으로 nbody
          // region 노드를 PM doc 에 만들면 NgwEditor 의 default empty paragraph 가 nbody 옆에
          // 남아 화면에 빈 라인이 추가되는 회귀 (사용자 보고). content.setData 는 doc 전체를
          // newHtml 로 set 하여 빈 paragraph 누적 없음.

          // 2026-05-21 — ImageNodeView race 회피 + wrapper force-set.
          //
          // NgwEditor 의 ImageNodeView 가 onLoad 시점에 view.dom 의 boundingClientRect 기반
          // editorMaxWidth 측정. SignatureEdit popup mount 직후 layout 이 안정되기 전 시점
          // 이면 작은 값 (예: 97px) 으로 측정되어 wrapper inline style.width 가 작게 적용
          // 되는 회귀 (사용자 보고). 두 단계로 보호:
          //   1) `waitForStableLayout` — editorContainer width >= 300px 까지 polling 후 setData.
          //   2) `scheduleImageDimensionRestore` — setData 후 100/500/1500/3000ms 시점에
          //      wrapper.style 이 EML 원본 크기와 다르면 force set. PM attrs 는 정상이지만
          //      wrapper React render 만 작게 표시되는 Case B1 회귀 보호.
          const imgDimensionMap = extractImageDimensions(newHtml)

          if (this.iframe != null) {
            this.iframe.style.visibility = 'visible'
          }

          this.waitForStableLayout(() => {
            this.editorAdapter.content.setData(newHtml)
            onLoaded()
            this.scheduleImageDimensionRestore(imgDimensionMap)
          })
        })
      } else {
        onLoaded()
      }
    } else {
      this.applyEditorSettings()
      this.initFocus()
    }

    this.editorAdapter.mail.checkData()
  }

  onSetDataEnd = (): void => {
    console.log(' --- onSetDataEnd (SignatureEdit)')
  }

  notifyNoInput = (): void => {
    // SignatureEdit 는 noInputTime 미설정 — 본 콜백은 발화되지 않는다.
  }

  onScrollChanged = (top: number, bottom: number): void => {
    console.log(' --- setScrollPos (SignatureEdit) : ', top, bottom)
    if (this.editorAdapter.lifecycle.isReady() !== true) return
    const hostWindow = this.editorAdapter.host.getWindow()
    const h = hostWindow.innerHeight
    if (top < 0) {
      hostWindow.scrollBy(0, top - h / 2)
    } else if (bottom > h) {
      hostWindow.scrollBy(0, bottom - h + 16)
    }
  }

  editorHeightChanged = (scrollHeight: number, diff: number): void => {
    console.log(' --- editorHeightChanged (SignatureEdit) : ', scrollHeight, diff)
  }

  /**
   * setData 후 ImageNodeView 의 layout race 로 PM doc 의 image attrs 가 자동 변형된 경우
   * EML 원본 width/height 로 복원. 100ms / 500ms / 1500ms 세 시점에 호출 — image onLoad
   * 가 발화하는 worst case timing 도 보호.
   *
   * 진단 로그도 함께 발화 (회귀 보고 시 layout 측정값 캡처 용도). 작은 width 회귀가 재발
   * 하면 사용자가 console 의 `[SignatureEdit.layoutDebug]` 로그를 확인.
   */
  /**
   * setData 호출을 editorContainer 의 layout (boundingClientRect.width) 이 안정될 때까지
   * polling 으로 미룬다. NgwEditor 모드에서는 iframe 이 없어 editorContainer 가 layout 측정
   * 의 source-of-truth.
   *
   * - 50ms 간격 polling
   * - width >= STABLE_WIDTH_THRESHOLD (300px) 이면 callback 호출
   * - 최대 3초 timeout 후에는 강제 진행 (무한 대기 방지)
   */
  private waitForStableLayout = (callback: () => void): void => {
    const STABLE_WIDTH_THRESHOLD = 300
    const MAX_WAIT_MS = 3000
    const POLL_INTERVAL_MS = 50
    const start = Date.now()

    const tryNow = (): void => {
      const container = this.editorContainer
      const width = container?.getBoundingClientRect().width ?? 0
      const elapsed = Date.now() - start
      if (width >= STABLE_WIDTH_THRESHOLD || elapsed >= MAX_WAIT_MS) {
        callback()
        return
      }
      setTimeout(tryNow, POLL_INTERVAL_MS)
    }
    tryNow()
  }

  /**
   * NgwEditor ImageNodeView 의 layout race 회귀 보호.
   *
   * ImageNodeView 의 React render 는 `wrapper.style.width = Math.min(attrs.width, getMaxWidth())`
   * 으로 계산. `getMaxWidth()` 가 view.dom 의 boundingClientRect.width 를 측정 — popup mount
   * 직후 layout 이 안정되기 전 시점이면 작은 값 (예: 97) 으로 측정되어 wrapper inline
   * style 이 작은 값으로 적용. 이후 view.dom layout 이 안정되어도 ImageNodeView 의 attrs
   * 변경 없으면 re-render 안 됨 → wrapper 영구 작은 크기.
   *
   * 본 메서드는 setData 후 100/500/1500/3000ms 시점에 wrapper.style.width 가 EML 원본
   * (srcMap.width) 과 5px 이상 다르면 wrapper.style 을 원본 크기로 force set. PM
   * transaction 이 아닌 DOM 직접 조작이므로 modified state / undo history 영향 없음.
   *
   * 추가 안전: `restoreImageDimensions` 호출로 PM attrs 자체도 변형된 케이스 (Case B2)
   * 도 복원.
   */
  private scheduleImageDimensionRestore = (
    srcMap: Map<string, { width: number; height: number }>,
  ): void => {
    if (srcMap.size === 0) return
    const restoreAt = (delayMs: number): void => {
      setTimeout(() => {
        const wrappers = Array.from(
          this.editorContainer?.querySelectorAll<HTMLElement>(
            '.image-node-view, .resize-tracker',
          ) ?? [],
        )
        wrappers.forEach((wrapperEl) => {
          const img = wrapperEl.querySelector<HTMLImageElement>('img')
          const src = img?.getAttribute('src')
          if (src == null) return
          const orig = srcMap.get(src)
          if (orig == null) return
          const styleWidth = parseInt(wrapperEl.style.width, 10)
          // 정상 (styleWidth ≈ orig.width) → skip. 5px 이내는 padding/border 미세 차이.
          if (Number.isFinite(styleWidth) && Math.abs(styleWidth - orig.width) < 5) return
          wrapperEl.style.width = orig.width + 'px'
          wrapperEl.style.height = orig.height + 'px'
        })
        this.editorAdapter.image.restoreImageDimensions(srcMap)
      }, delayMs)
    }
    restoreAt(100)
    restoreAt(500)
    restoreAt(1500)
    restoreAt(3000)
  }

  hasNativeToolbar = (): HasNativeToolbarResult => {
    return 'props'
  }

  toolbarPropsChanged = (payload: ToolbarPropsChangePayload): void => {
    this.onToolbarPropChanged(payload.formattedString, payload.buttonString)
  }

  showCreateLinkView = (url: string, text: string, type: string): void => {
    if (this.editorAdapter.lifecycle.isReady() !== true) return
    editorToolbar.insertLink(url, text, type, {
      addLink: (linkUrl: string, linkText: string) => {
        this.editorAdapter.host.focus()
        this.editorAdapter.cursor.focus()
        const isHTML = type !== 'text'
        // 프로토콜 미지정 URL 은 라이브러리 setLink(withDefaultProtocol)가 https:// 를 자동 부여한다.
        this.editorAdapter.state.setLink(linkUrl, linkText, isHTML)
        this.editorAdapter.host.focus()
      },
      removeLink: () => {
        this.editorAdapter.host.focus()
        this.editorAdapter.cursor.focus()
        this.editorAdapter.command.run('unlink')
      },
      focus: () => {
        this.editorAdapter.host.focus()
        this.editorAdapter.cursor.focus()
      },
    })
  }

  openImageChooser = (callbackKey?: string): void => {
    console.log(' - openImageChooser (SignatureEdit) : ', callbackKey)

    const onSizeError = (curSize: number, filelimit: number): void => {
      console.log(' - onSizeError : ', curSize, filelimit)
      const limit = formatBytes(this.attachmentsLimit, 1, false, 2)
      const message = i18n.t('email.attachment.limitWarning', { limit })
      omMessage.warning(message)
    }

    const acceptedType = 'image/png, image/jpeg, image/bmp'

    const totalSize = this.getTotalAttachmentSize()
    const sizeLimit = this.attachmentsLimit - totalSize

    const onChange = async (file: File, _idx: number, _total: number): Promise<void> => {
      console.log(' - onChange : ', file)
      if (editorType === 'ngw') {
        // PR-N9a-1 — NgwEditor schema 의 parseDOM regex 가 blob URL 을 drop 하므로
        // attachBlobAsDataUrl 로 data URL 변환 후 PM 에 직접 insert.
        const { uri, originalBlobUrl } = await this.editorAdapter.image.attachBlobAsDataUrl(file, {
          name: file.name,
        })
        this.createImageInfo(file, originalBlobUrl, callbackKey)
        this.editorAdapter.image.insert(uri)
        return
      }
      const url = this.getBlobURL(file, file.name)
      this.createImageInfo(file, url, callbackKey)
    }

    openFile(onChange, null, acceptedType, sizeLimit, onSizeError, false)
  }

  // OFM-224: 서명 크기 상한(500KB). 가져오기는 HTML 파일 크기, 저장은 최종 전송 EML(content) 크기에
  // 적용한다. officemail-platform 의 MAX_HTML_SIZE 와 동일한 500KB 기준이다.
  signatureSizeLimit: number = 500 * 1024

  // 서명 편집기 HTML 파일 가져오기. 부모 editSignature 모달 footer 의 '가져오기' 버튼이 innerRef 를
  // 통해 본 메서드를 호출. openImageChooser 와 동일하게 openFile 재사용.
  // .html 파일만(500KB 제한) 선택 → getSafeHTML 정제 → content.setData 로 전체 교체.

  importHtmlFile = (): void => {
    // 에디터 미준비 상태면 파일 대화상자를 열어도 setData 를 반영할 수 없으므로 진입 차단.
    if (this.editorAdapter.lifecycle.isReady() !== true) return

    const onSizeError = (fileSize: number): void => {
      // fixedUnit='KB' → sizes 인덱스 1(KB) 고정. 500*1024 → "500 KB". (0 을 주면 'bytes' 단위로
      // "512000 bytes" 가 되므로 반드시 'KB' 지정.)
      const limit = formatBytes(this.signatureSizeLimit, 0, false, 'KB')
      // 현재 파일 크기도 KB 로 고정 표기하되, limit(정수 500 KB)과 시각적으로 겹치지 않도록 소수 1자리.
      const size = formatBytes(fileSize, 1, false, 'KB')
      errorBase({
        title: i18n.t('signature.import.errorTitle'),
        content: i18n.t('signature.import.sizeError', { limit, size }),
        width: 450,
      })
    }

    const onChange = async (file: File): Promise<void> => {
      if (file.name.toLowerCase().endsWith('.html') !== true) {
        // openFile 의 accept 는 다이얼로그 소프트 필터일 뿐이고 processOpenedFile 의 타입 검사가
        // 실제 거부를 하지 않으므로 확장자를 직접 재검증한다.
        errorBase({
          title: i18n.t('signature.import.errorTitle'),
          content: i18n.t('signature.import.extError'),
          width: 450,
        })
        return
      }

      let buffer: ArrayBuffer
      try {
        buffer = await file.arrayBuffer()
      } catch (err) {
        console.error('SignatureEdit: HTML import read failed', err)
        errorBase({
          title: i18n.t('signature.import.errorTitle'),
          content: i18n.t('signature.import.readError'),
          width: 450,
        })
        return
      }

      // file.text() 는 인코딩과 무관하게 UTF-8 로 디코딩하며 잘못된 바이트를 조용히 U+FFFD 로
      // 치환한다(EUC-KR 등이 mojibake 로 삽입됨). fatal 모드로 엄격 디코딩해 UTF-8 이 아니면 거부.
      // (UTF-8 BOM 은 TextDecoder 가 자동 제거.)
      let text: string
      try {
        text = new TextDecoder('utf-8', { fatal: true }).decode(new Uint8Array(buffer))
      } catch {
        errorBase({
          title: i18n.t('signature.import.errorTitle'),
          content: i18n.t('signature.import.encodingError'),
          width: 450,
        })
        return
      }

      await this.applyImportedHtml(text)
    }

    openFile(onChange, null, '.html,text/html', this.signatureSizeLimit, onSizeError, false)
  }

  private applyImportedHtml = async (rawHtml: string): Promise<void> => {
    const doImport = async (): Promise<void> => {
      if (this.editorAdapter.lifecycle.isReady() !== true) return
      const safe = await getSafeHTML(rawHtml)
      await this.editorAdapter.content.setData(safe)
      // 가져온 HTML 에 fold 된 quoted 마크업이 포함될 수 있으므로 서명 편집기 규칙대로 펼친다.
      this.editorAdapter.mail.setQuotedCollapsed(false)
      this.editorAdapter.host.focus()
      // init 경로와 달리 resetChangedState 를 호출하지 않아 dirty 유지 → 저장 버튼 enable.
      // 어댑터 onChange 발화 타이밍에 의존하지 않도록 modified 추적을 즉시 명시 호출한다.
      this.onInputChanged()
    }

    if (this.isEmpty() === true) {
      await doImport()
      return
    }

    // 편집 중인 내용이 있을 때만 덮어쓰기 확인. editSignature 모달(zIndex 기본 1800) 위에
    // 표시되도록 zIndex 를 높인다.
    confirmBase({
      title: i18n.t('signature.import.overwriteTitle'),
      content: i18n.t('signature.import.overwriteContent'),
      okText: i18n.t('common.button.ok'),
      cancelText: i18n.t('common.button.cancel'),
      zIndex: 1900,
      // 프로미스 반환 → 확인 버튼이 import 완료까지 loading 유지 후 닫힘(중간 깜빡임 방지).
      onOk: () => doImport(),
    })
  }

  getProcessedImage = (
    src: string,
    size: number,
    flip: number,
    rotate: number,
  ): Promise<string> => {
    console.log(' - getProcessedImage (SignatureEdit) : ', src, size, flip, rotate)

    const imgInfo = this.processImageMap[src]
    const { imgObj, infoList, file } = imgInfo
    const imgIdx = rotate * 4 + flip

    const subImgInfo = infoList[imgIdx]
    if (subImgInfo == null) {
      return new Promise<string>((resolve) => {
        const canvas = document.createElement('canvas')
        const canvasContext = canvas.getContext('2d')!

        const nw = imgObj.naturalWidth
        const nh = imgObj.naturalHeight

        let width = Math.max(nw, nh)
        let height = width

        let top = 0
        let left = 0
        if (nw > nh) {
          top = (nw - nh) / 2
        } else {
          left = (nh - nw) / 2
        }

        canvas.width = width
        canvas.height = height

        const degrees = rotate * 90
        const flipH = (flip >> 1) & 1
        const flipV = (flip >> 0) & 1
        const scaleH = flipH ? -1 : 1
        const scaleV = flipV ? -1 : 1
        const posX = flipH ? width * -1 : 0
        const posY = flipV ? height * -1 : 0

        canvasContext.save()

        if (rotate === 1 || rotate === 3) {
          canvasContext.translate(-top, -left)
          width = nh
          height = nw
        } else {
          canvasContext.translate(-left, -top)
          width = nw
          height = nh
        }

        canvasContext.translate(canvas.width / 2, canvas.height / 2)
        canvasContext.rotate((degrees * Math.PI) / 180)
        canvasContext.translate(-canvas.width / 2, -canvas.height / 2)
        canvasContext.scale(scaleH, scaleV)
        canvasContext.drawImage(imgObj, posX + left, posY + top, nw, nh)
        canvasContext.restore()

        const imgData = canvasContext.getImageData(0, 0, width, height)

        canvas.width = width
        canvas.height = height

        canvasContext.putImageData(imgData, 0, 0)

        canvas.toBlob(
          (blob) => {
            if (blob != null && infoList[imgIdx] == null) {
              const pos = file.name.lastIndexOf('.')
              const ext = file.name.substring(pos + 1)
              const newName = `${file.name.substring(0, pos)}_${imgIdx}.${ext}`

              infoList[imgIdx] = {
                blob,
                url: this.getBlobURL(blob, newName),
              }
            }

            this.srcImageMap[infoList[imgIdx]!.url] = imgInfo

            resolve(infoList[imgIdx]!.url)
          },
          'image/png',
          1,
        )
      })
    }

    return Promise.resolve(subImgInfo.url)
  }

  createProcessedImage = (src: string, base64Data: string, mimetype: string): string => {
    console.log(' - createProcessedImage (SignatureEdit) : ', src, base64Data, mimetype)

    let blob: File
    if (base64Data.indexOf('data:') === 0) {
      blob = this.dataURLtoBlob(base64Data)
    } else {
      blob = this.base64ToBlob(base64Data, mimetype)
    }

    let fileName: string | null = null
    if (src != null && src.length > 0) {
      try {
        const url = new URL(src)
        if (url.pathname.length > 1) {
          const pos1 = url.pathname.lastIndexOf('/')
          fileName = url.pathname.substring(pos1 + 1)
        }
      } catch {
        // Invalid URL, continue with null fileName
      }
    }

    if (fileName == null) {
      const uniqueId = getUniqueKey('editer-img')
      fileName = `img-${uniqueId}`
    }

    const file = new File([blob], fileName, { type: mimetype })
    const url = this.getBlobURL(blob, 'noname')

    this.createImageInfo(file, url)

    return url
  }

  createProcessedImageList = (srcListStr: string): Promise<string> => {
    console.log(' - createProcessedImageList (SignatureEdit) : ', srcListStr)
    const srcList = srcListStr.split('\t')

    const downloadList: Promise<Attachment>[] = []

    srcList.forEach((src) => {
      if (src.indexOf('cid:') === 0) {
        const cid = src.substring(4)
        const attachment = this.getInlineAttachment(cid)
        if (attachment != null && attachment.download) {
          const promise = attachment.download().then((ret) => {
            attachment.downloadObj = ret as any
            return attachment
          })
          downloadList.push(promise)
        }
      }
    })

    return Promise.all(downloadList).then((retList) => {
      const urlList = retList.map((attachment) => {
        const ret = attachment.downloadObj!
        const url = this.getBlobURL(ret.blob, ret.file.name)
        const imageInfo = this.createImageInfo(ret.file, url)
        imageInfo.attachment = attachment
        return url
      })

      return urlList.join('\t')
    })
  }

  onPreProcess = (evt: EditorPreProcessEvent): void => {
    if (evt.format === 'raw' && evt.get === true) {
      console.log(' ---- PreProcess (SignatureEdit) ---- ', evt)

      const oldInlineAttachmentMap = { ...this.inlineAttachmentMap }

      this.inlineAttachmentMap = {}

      const imgList = evt.node.querySelectorAll('img')
      imgList.forEach((img: HTMLImageElement) => {
        const src = img.getAttribute('src')
        if (src != null && this.srcImageMap[src] != null) {
          const imgInfo = this.srcImageMap[src]
          const { infoList, file } = imgInfo
          const infoIdx = infoList.findIndex((info: any) => info != null && info.url === src)
          if (infoIdx > -1) {
            const info = infoList[infoIdx]!

            if (infoIdx === 0 && imgInfo.attachment != null) {
              const { cid } = imgInfo.attachment.file
              img.src = `cid:${cid}`
              imgInfo.attachment.isUsed = true
              this.addInlineAttachment(imgInfo.attachment, cid)
            } else {
              let newName = file.name
              if (infoIdx > 0) {
                const pos = file.name.lastIndexOf('.')
                const ext = file.name.substring(pos + 1)
                newName = `${file.name.substring(0, pos)}_${infoIdx}.${ext}`
              }
              const newFile = new File([info.blob], newName, {
                lastModified: new Date().getTime(),
                type: info.blob.type,
              })

              const pos1 = src.lastIndexOf('/')
              const blobText = src.substring(pos1 + 1)

              const domain = location.host
              const cid = `${blobText}@${domain}`

              img.src = `cid:${cid}`

              const attachment: any = this.attachmentHelper.createAttachment(
                newFile,
                cid,
                this as any,
              )
              attachment.isUsed = true

              this.addInlineAttachment(attachment, cid)
            }
          }
        } else if (src != null && src.indexOf('cid:') === 0) {
          const cid = src.substring(4)
          const attachment = oldInlineAttachmentMap[cid]
          if (attachment != null) {
            attachment.isUsed = true
            this.addInlineAttachment(attachment, cid)
          }
        } else if (src != null && src.indexOf('data:image/') === 0) {
          // NgwEditor 내장 picker / paste / drag 경로로 PM 에 직접 주입된 dataUrl 이미지 처리.
          // 어댑터 urlMap / srcImageMap 어디에도 등록되지 않은 상태로 들어오므로 본 핸들러가
          // dataUrl → blob → cid 변환 + inline attachment 등록까지 책임진다.
          const { cid, attachmentFile } = resolveOrCreateDataUrlCid(src, this.editorAdapter.image, {
            host: location.host,
            createUuid: jamExt.createUuid,
          })
          img.src = `cid:${cid}`
          const attachment: any = this.attachmentHelper.createAttachment(
            attachmentFile,
            cid,
            this as any,
          )
          attachment.isUsed = true
          this.addInlineAttachment(attachment, cid)
        }
      })
    }
  }

  /**
   * EmailForm.onEditorPaste 평행 (apps/owma/src/pages/main/components/EmailForm/index.tsx L3877).
   *
   * NgwEditor 의 image NodeSpec parseDOM regex 가 cid:/blob: src 를 schema 단계에서 drop 하므로
   * paste 직후 본 핸들러에서 data URL 로 변환해 PM 에 insert 한다. NgwEditorLifecycle.attachPasteListener
   * 가 capture phase 에서 adapter handler.onEditorPaste 로 forward (라이브러리 버그 우회).
   *
   * 2026-05-20 회귀 fix — 기존에는 SignatureEdit 에 본 핸들러가 없어 서명 편집 이미지 paste 가
   * 침묵 실패 (NgwEditor 내장 paste 가 schema drop 으로 이미지 누락). EmailForm 과 동일한
   * `convertPastedImageSources` helper 로 cid attachment lookup + fetch + items fallback 적용.
   */
  onEditorPaste = (event: ClipboardEvent): void => {
    const clipboardData = event.clipboardData
    if (clipboardData == null) return

    const html = clipboardData.getData('text/html')
    const doc = html.length > 0 ? new DOMParser().parseFromString(html, 'text/html') : null
    const imgs = doc != null ? Array.from(doc.querySelectorAll('img')) : []
    const needsConversion = imgs.some((img) => {
      const src = img.getAttribute('src') ?? ''
      return src.startsWith('blob:') || src.startsWith('cid:')
    })

    const imageItems = Array.from(clipboardData.items).filter(
      (item) => item.kind === 'file' && item.type.startsWith('image/'),
    )

    // image item 은 "순수 이미지 붙여넣기(HTML 없음)" 일 때만 커스텀 삽입 대상으로 쓴다.
    // Excel/Word 등은 표 HTML(text/html)과 함께 표를 렌더한 image/png 를 clipboard 에 넣으므로,
    // HTML 이 있으면 image item 으로 가로채지 말고 needsConversion(본문 내 blob:/cid: img)일 때만
    // 가로챈다. 그 외에는 라이브러리 기본 paste 경로에 맡겨 표 구조 손실을 막는다.
    const needsImageItemInsert = doc == null && imageItems.length > 0

    if (!needsConversion && !needsImageItemInsert) return

    event.preventDefault()
    event.stopPropagation()

    void this.convertPastedHtmlAndInsert(doc, imgs, imageItems)
  }

  private convertPastedHtmlAndInsert = async (
    doc: Document | null,
    imgs: HTMLImageElement[],
    imageItems: DataTransferItem[],
  ): Promise<void> => {
    if (doc == null && imageItems.length > 0) {
      const promises = imageItems.map(async (item) => {
        const blob = item.getAsFile()
        if (blob == null) return ''
        try {
          const dataUrl = await blobToDataUrl(blob)
          const escaped = dataUrl.replace(/"/g, '&quot;')
          return `<img src="${escaped}" />`
        } catch {
          return ''
        }
      })
      const htmlParts = await Promise.all(promises)
      const combined = htmlParts.filter((s) => s.length > 0).join('')
      if (combined.length > 0) this.editorAdapter.content.insertContent(combined)
      return
    }

    if (doc == null) return

    await convertPastedImageSources(imgs, imageItems, {
      attachmentLookup: (cid) => this.inlineAttachmentMap[cid],
    })

    const html = doc.body.innerHTML
    if (html.length === 0) return
    this.editorAdapter.content.insertContent(html)
  }

  render(): React.ReactElement {
    const { invisible } = this.state
    const { readonly } = this.props

    const isReadOnly = readonly === true

    let elem: React.ReactElement | null = null

    if (isReadOnly === true) {
      elem = this.getReadOnlyElem()
    } else {
      // EmailForm 평행 (L4459) — NgwEditor 모드에서는 외부 toolbar 숨김. NgwEditor 가 내장 toolbar
      // 를 자체 렌더하므로 외부 toolbar 가 함께 표시되면 이중 toolbar 회귀 (사용자 보고
      // 2026-05-19 — 외부 toolbar 는 동작하나 NgwEditor 내장 toolbar 는 클릭해도 무반응).
      const editorToolbarElem = editorType !== 'ngw' ? this.getEditorToolbarElem() : null
      const editorElem = this.getEditElem()

      elem = (
        <React.Fragment>
          {editorToolbarElem}
          <div className={classNames(styles.editorWrapper)}>{editorElem}</div>
          <div className={classNames(styles.loadingAni, { show: invisible === true })}>
            <img src={syncAniGIF} alt="sync" />
          </div>
        </React.Fragment>
      )
    }

    return <div className={classNames(styles.signatureEdit, 'signatureEdit')}>{elem}</div>
  }
}

export default SignatureEdit
